import {useEffect, useMemo, useRef, useState} from 'react';
import {
  Box,
  Button,
  Flex,
  HStack,
  Spinner,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import {TablerArrowRight, TablerCheck} from '../../icons';
import {
  fetchVideoPlaybackUrl,
  type UniversityVideo,
} from '../../services/universityService';
import ModalFrame from '../common/ModalFrame';
import MetaPill from '../common/MetaPill';
import usePlannerStyles from '../planner/usePlannerStyles';
import UniversityPoster from './UniversityPoster';
import {
  captureUniversity,
  captureVideoClosedFromSession,
  captureVideoCompleted,
  captureVideoOpened,
  captureVideoStarted,
  createWatchSession,
  flushPlayTime,
  markPlaying,
  maybeCaptureProgressMilestones,
  notePosition,
  watchSnapshot,
  type UniversityVideoCloseReason,
  type UniversityWatchSession,
} from './universityAnalytics';

// ─── Opalite University shared player ────────────────────────────────────────
//
// One modal for every surface. `videos` is the playback queue: a single entry
// for a one-off watch, or the ordered Getting Started playlist.
//
// Video files live in a private bucket, so the modal resolves a playback URL
// at play time: by default it exchanges the caller's Firebase token for a
// short-lived signed URL (fetchVideoPlaybackUrl); the pre-auth /start page
// passes its own `resolveSrc` that uses the unauthenticated public-stream
// redirect. URLs are resolved per open — never persisted — so a day-old tab
// can't hold an expired link.
//
// Placeholder entries (hasVideo === false) render a designed "coming soon"
// frame with the video's outline; advancing past a placeholder counts as
// watching it, otherwise tips and the guided playlist could never be
// completed pre-recording.

export const formatVideoDuration = (durationSec: number): string => {
  const mins = Math.floor(durationSec / 60);
  const secs = durationSec % 60;
  if (mins === 0) return `${secs}s`;
  return secs === 0
    ? `${mins} min`
    : `${mins}:${String(secs).padStart(2, '0')}`;
};

const UniversityVideoModal = ({
  isOpen,
  onClose,
  videos,
  initialIndex = 0,
  playlistLabel = '',
  source = 'library',
  resolveSrc,
  onWatched,
}: {
  isOpen: boolean;
  onClose: () => void;
  videos: UniversityVideo[];
  initialIndex?: number;
  playlistLabel?: string;
  source?: string;
  /** Playback URL resolver; defaults to the authenticated signed-URL flow. */
  resolveSrc?: (video: UniversityVideo) => Promise<string> | string;
  onWatched?: (videoId: string) => void;
}) => {
  const styles = usePlannerStyles();
  // Phones get the platform-native video pattern — a full-screen player with
  // the video pinned up top, scrollable text, and the action bar at the
  // bottom by the thumb — instead of a floating desktop dialog that overflows
  // in landscape. useBreakpointValue because Chakra's Modal `size` doesn't
  // take responsive objects.
  const isFullScreen = useBreakpointValue({base: true, md: false}) ?? false;
  const [index, setIndex] = useState(initialIndex);
  const [srcById, setSrcById] = useState<Record<string, string>>({});
  const [srcFailedId, setSrcFailedId] = useState<string | null>(null);
  // A video can complete twice in one sitting (natural `ended` + the Done /
  // Next button). Report each completion once per open.
  const completedIdsRef = useRef<Set<string>>(new Set());
  const watchSessionRef = useRef<UniversityWatchSession | null>(null);
  const videoElRef = useRef<HTMLVideoElement | null>(null);
  // Keep the latest leave handler available to the unmount cleanup without
  // re-subscribing effects on every render.
  const leaveCurrentRef = useRef<(reason: UniversityVideoCloseReason) => void>(
    () => undefined,
  );

  const isPlaylist = videos.length > 1;
  const video = videos[Math.min(index, videos.length - 1)];
  const nextVideo = isPlaylist ? videos[index + 1] : undefined;
  const isPlaceholder = Boolean(video) && !video.hasVideo;
  const src = video ? srcById[video.id] : undefined;
  const srcFailed = Boolean(video) && srcFailedId === video.id;

  const sessionOpts = useMemo(
    () => ({
      source,
      playlistIndex: index,
      playlistLength: videos.length,
    }),
    [source, index, videos.length],
  );

  useEffect(() => {
    if (isOpen) {
      setIndex(initialIndex);
      completedIdsRef.current = new Set();
    }
  }, [isOpen, initialIndex]);

  useEffect(() => {
    if (!isOpen || !video) return;
    watchSessionRef.current = createWatchSession(video.id);
    captureVideoOpened(video, sessionOpts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, video?.id]);

  // Resolve the playback URL for the current video just-in-time.
  useEffect(() => {
    if (!isOpen || !video?.hasVideo || srcById[video.id]) return undefined;
    let active = true;
    setSrcFailedId(null);
    const resolver =
      resolveSrc || ((v: UniversityVideo) => fetchVideoPlaybackUrl(v.id));
    Promise.resolve(resolver(video))
      .then(url => {
        if (active) setSrcById(prev => ({...prev, [video.id]: url}));
      })
      .catch(err => {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[university] playback url fetch failed:', err);
        }
        if (active) {
          setSrcFailedId(video.id);
          captureUniversity('university_video_load_failed', {
            video_id: video.id,
            video_title: video.title,
            source,
          });
        }
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, video?.id]);

  // Flush watch stats if the modal unmounts mid-play (route change, etc.).
  useEffect(() => {
    if (!isOpen) return undefined;
    return () => {
      leaveCurrentRef.current('navigate_away');
    };
  }, [isOpen]);

  const stepLabel = useMemo(() => {
    if (!isPlaylist) return '';
    return `${index + 1} of ${videos.length}`;
  }, [isPlaylist, index, videos.length]);

  if (!video) return null;

  const leaveCurrent = (reason: UniversityVideoCloseReason) => {
    const session = watchSessionRef.current;
    if (!session || session.videoId !== video.id) return;
    captureVideoClosedFromSession(session, video, {
      ...sessionOpts,
      closeReason: reason,
    });
  };
  leaveCurrentRef.current = leaveCurrent;

  const completeCurrent = () => {
    if (completedIdsRef.current.has(video.id)) return;
    completedIdsRef.current.add(video.id);
    const session = watchSessionRef.current;
    if (session && session.videoId === video.id) {
      flushPlayTime(session);
      session.completed = true;
      if (isPlaceholder) {
        session.maxPositionSec = video.durationSec;
      }
      captureVideoCompleted(watchSnapshot(session, video, sessionOpts));
    } else {
      captureVideoCompleted({
        video_id: video.id,
        video_title: video.title,
        duration_sec: video.durationSec,
        watch_time_sec: 0,
        max_position_sec: isPlaceholder ? video.durationSec : 0,
        percent_watched: isPlaceholder ? 100 : 0,
        completed: true,
        placeholder: isPlaceholder,
        source,
        playlist_index: index,
        playlist_length: videos.length,
        is_playlist: isPlaylist,
      });
    }
    onWatched?.(video.id);
  };

  const handleEnded = () => {
    const session = watchSessionRef.current;
    if (session && session.videoId === video.id) {
      flushPlayTime(session);
      notePosition(session, video.durationSec);
    }
    completeCurrent();
  };

  const handleAdvance = () => {
    // Deliberate progression counts as consumption for placeholders (real
    // videos already reported on `ended`; markWatched upstream dedupes).
    completeCurrent();
    if (nextVideo) {
      leaveCurrent('next');
      setIndex(index + 1);
    } else {
      leaveCurrent('done');
      onClose();
    }
  };

  const handleDismiss = () => {
    leaveCurrent('dismiss');
    onClose();
  };

  const handlePlay = () => {
    const session = watchSessionRef.current;
    if (!session || session.videoId !== video.id) return;
    markPlaying(session);
    if (!session.started) {
      session.started = true;
      captureVideoStarted(watchSnapshot(session, video, sessionOpts));
    }
  };

  const handlePause = () => {
    const session = watchSessionRef.current;
    if (!session || session.videoId !== video.id) return;
    flushPlayTime(session);
  };

  const handleTimeUpdate = () => {
    const el = videoElRef.current;
    const session = watchSessionRef.current;
    if (!el || !session || session.videoId !== video.id) return;
    notePosition(session, el.currentTime);
    maybeCaptureProgressMilestones(session, video, sessionOpts);
  };

  const retrySrc = () => {
    captureUniversity('university_video_retry_clicked', {
      video_id: video.id,
      video_title: video.title,
      source,
    });
    setSrcFailedId(null);
    const resolver =
      resolveSrc || ((v: UniversityVideo) => fetchVideoPlaybackUrl(v.id));
    Promise.resolve(resolver(video))
      .then(url => setSrcById(prev => ({...prev, [video.id]: url})))
      .catch(() => {
        setSrcFailedId(video.id);
        captureUniversity('university_video_load_failed', {
          video_id: video.id,
          video_title: video.title,
          source,
          retried: true,
        });
      });
  };

  return (
    <ModalFrame
      isOpen={isOpen}
      onClose={handleDismiss}
      isCentered={!isFullScreen}
      size={isFullScreen ? 'full' : '3xl'}
      title={
        playlistLabel ? (
          <HStack spacing={3}>
            <Text as="span">{playlistLabel}</Text>
            {stepLabel && (
              <MetaPill flexShrink={0} borderRadius="md">
                {stepLabel}
              </MetaPill>
            )}
          </HStack>
        ) : null
      }
      contentProps={
        isFullScreen
          ? {
              mx: 0,
              my: 0,
              borderRadius: 0,
              overflow: 'hidden',
              // Keep the header clear of the notch as an installed PWA.
              pt: 'env(safe-area-inset-top, 0px)',
            }
          : {
              overflow: 'hidden',
              // A theater-style surface: as wide as the viewport comfortably
              // allows (the media height clamp below keeps the controls and
              // footer on screen), without sending the user to fullscreen.
              maxW: 'min(76rem, calc(100vw - 3rem))',
            }
      }
      bodyProps={{
        pt: playlistLabel ? 0 : 10,
        pb: 4,
        // Full-screen: the body owns the scroll (long descriptions, small
        // landscape viewports) while the video and footer stay put.
        ...(isFullScreen ? {flex: '1 1 auto', overflowY: 'auto', minH: 0} : {}),
      }}
      footerProps={
        isFullScreen
          ? {pb: 'max(1rem, env(safe-area-inset-bottom, 0px))'}
          : undefined
      }
      footer={
        <Flex w="100%" align="center" justify="space-between" gap={3}>
          <Text fontSize="sm" color={styles.subtleText} noOfLines={1}>
            {nextVideo ? `Next: ${nextVideo.title}` : ''}
          </Text>
          <HStack spacing={2} flexShrink={0}>
            {isPlaylist && nextVideo ? (
              <Button
                size="sm"
                borderRadius="lg"
                bg="opalite.600"
                color="white"
                fontWeight="medium"
                rightIcon={<TablerArrowRight size={16} aria-hidden="true" />}
                onClick={handleAdvance}
                _hover={{bg: 'opalite.700'}}
                _active={{bg: 'opalite.700'}}
                _focusVisible={styles.focusOutlineStyles}
              >
                Next video
              </Button>
            ) : (
              <Button
                size="sm"
                borderRadius="lg"
                bg="opalite.600"
                color="white"
                fontWeight="medium"
                rightIcon={<TablerCheck size={16} aria-hidden="true" />}
                onClick={handleAdvance}
                _hover={{bg: 'opalite.700'}}
                _active={{bg: 'opalite.700'}}
                _focusVisible={styles.focusOutlineStyles}
              >
                Done
              </Button>
            )}
          </HStack>
        </Flex>
      }
    >
      <Box borderRadius="xl" overflow="hidden" bg="black">
        {isPlaceholder ? (
          <Box
            position="relative"
            sx={{aspectRatio: '16 / 9', maxHeight: '64dvh'}}
          >
            <UniversityPoster
              video={video}
              iconSize={30}
              showComingSoon
              position="absolute"
              inset={0}
            />
            <Flex
              position="absolute"
              inset={0}
              direction="column"
              align="center"
              justify="flex-end"
              px={{base: 4, md: 10}}
              pb={{base: 4, md: 6}}
              textAlign="center"
              pointerEvents="none"
            >
              <Text
                color="whiteAlpha.900"
                fontSize={{base: 'xs', md: 'sm'}}
                lineHeight="1.5"
                textShadow="0 1px 8px rgba(7, 0, 77, 0.45)"
                noOfLines={3}
              >
                We&apos;re recording this video now. Here&apos;s what it will
                cover:
              </Text>
            </Flex>
          </Box>
        ) : src ? (
          <Box
            key={video.id}
            as="video"
            ref={videoElRef}
            controls
            autoPlay
            playsInline
            preload="metadata"
            src={src}
            poster={video.posterUrl || undefined}
            onPlay={handlePlay}
            onPause={handlePause}
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleEnded}
            w="100%"
            display="block"
            mx="auto"
            sx={{
              aspectRatio: '16 / 9',
              objectFit: 'contain',
              // Clamp media height to the viewport so the controls, title,
              // and footer always stay reachable: on landscape phones a
              // full-width 16:9 video overflows outright, and on laptops the
              // theater-width modal would otherwise push its footer off
              // screen. The <video> letterboxes inside the clamp.
              maxHeight: isFullScreen ? '60dvh' : '64dvh',
            }}
          />
        ) : (
          <Flex
            align="center"
            justify="center"
            direction="column"
            gap={3}
            sx={{aspectRatio: '16 / 9', maxHeight: '64dvh'}}
          >
            {srcFailed ? (
              <>
                <Text color="whiteAlpha.800" fontSize="sm">
                  We couldn&apos;t load this video.
                </Text>
                <Button
                  size="sm"
                  variant="outline"
                  borderRadius="lg"
                  color="white"
                  borderColor="whiteAlpha.500"
                  onClick={retrySrc}
                  _hover={{bg: 'whiteAlpha.200'}}
                >
                  Try again
                </Button>
              </>
            ) : (
              <Spinner color="whiteAlpha.800" thickness="3px" size="lg" />
            )}
          </Flex>
        )}
      </Box>

      <Flex mt={4} align="flex-start" justify="space-between" gap={3}>
        <Box minW={0}>
          <Text
            fontSize="lg"
            fontWeight="semibold"
            color={styles.textColor}
            lineHeight="1.3"
          >
            {video.title}
          </Text>
          <Text fontSize="sm" color={styles.subtleText} mt={1} lineHeight="1.6">
            {video.description}
          </Text>
        </Box>
        <MetaPill flexShrink={0} mt={1} borderRadius="md">
          {formatVideoDuration(video.durationSec)}
        </MetaPill>
      </Flex>
    </ModalFrame>
  );
};

export default UniversityVideoModal;
